import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, Calendar, ChevronRight, Truck } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { formatCOP } from '@/utils/format';
import { useAuth } from '@/context/AuthContext';
import { MyCoupons } from '@/components/account/MyCoupons';
import { PetBirthday } from '@/components/account/PetBirthday';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  pending: { label: 'Pendiente', color: 'bg-[#F5E6D3] text-[#8B4513]' },
  paid: { label: 'Pagado', color: 'bg-[#2A5C43]/10 text-[#2A5C43]' },
  processing: { label: 'En proceso', color: 'bg-blue-100 text-blue-800' },
  shipped: { label: 'Enviado', color: 'bg-[#D47A4A]/10 text-[#D47A4A]' },
  delivered: { label: 'Entregado', color: 'bg-[#2A5C43]/20 text-[#2A5C43]' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' }
};

const MyOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/orders/my-orders`, { withCredentials: true });
        setOrders(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12">
      <div className="mb-10">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Mi cuenta</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1C1917] tracking-tight">
          Hola, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-[#57534E] mt-2">Aquí puedes ver tus cupones y el historial de tus pedidos</p>
      </div>

      <MyCoupons />

      <PetBirthday />

      <h2 className="font-display font-semibold text-lg text-[#1C1917] mb-4">Mis pedidos</h2>
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-white rounded-2xl border border-[#E7E5E4] animate-pulse" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-3xl border border-[#E7E5E4] p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-[#F7F2E8] flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-10 h-10 text-[#8BA58F]" />
          </div>
          <p className="font-display text-xl text-[#1C1917] mb-2">Aún no tienes pedidos</p>
          <p className="text-sm text-[#57534E] mb-6">Explora nuestro catálogo y encuentra el alimento ideal</p>
          <Button asChild className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6" data-testid="myorders-empty-cta">
            <Link to="/perros">Empezar a comprar</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const status = statusLabels[order.status] || statusLabels.pending;
            const date = new Date(order.created_at);
            return (
              <div
                key={order.id}
                className="bg-white rounded-2xl border border-[#E7E5E4] p-6 hover:shadow-md transition-all"
                data-testid={`order-${order.order_number}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#F7F2E8] flex items-center justify-center shrink-0">
                    <Package className="w-6 h-6 text-[#2A5C43]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-display font-semibold text-[#1C1917]">Pedido {order.order_number}</p>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-[#57534E]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {date.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                      <span>•</span>
                      <span>{order.items?.length || 0} {order.items?.length === 1 ? 'producto' : 'productos'}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-[#8BA58F] font-semibold">Total</p>
                    <p className="font-display font-bold text-xl text-[#2A5C43]">{formatCOP(order.total)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#F5F5F4]">
                  <Button asChild variant="outline" size="sm" className="rounded-full border-[#2A5C43] text-[#2A5C43]">
                    <Link
                      to={`/rastreo/${order.order_number}?email=${encodeURIComponent(order.customer_email || user?.email || '')}`}
                      data-testid={`track-order-${order.order_number}`}
                    >
                      <Truck className="w-4 h-4 mr-2" /> Rastrear pedido
                    </Link>
                  </Button>
                  {order.status === 'delivered' && (
                    <Button asChild size="sm" className="rounded-full bg-[#D47A4A] hover:bg-[#B86538] text-white">
                      <Link
                        to={`/resena/${order.order_number}?email=${encodeURIComponent(order.customer_email || user?.email || '')}`}
                        data-testid={`review-order-${order.order_number}`}
                      >
                        Dejar reseña <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyOrders;
